import { useQuery } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiClient } from '@/lib/api-client';
import { CardShell, EmptyState } from '../cards/card-shell';
import { formatBrlCompact } from './format';

/**
 * Bar chart 6 meses: receita realizada (verde) vs despesa realizada
 * (vermelha). Usa Recharts (ja parte do bundle do dashboard).
 */
export function ChartReceitaDespesaWidget() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['financial', 'stats', 'chart', 6],
    queryFn: () => apiClient.financialStatsChart(6),
    staleTime: 1000 * 60,
  });

  const rows = (data ?? []).map((d) => ({
    month: d.month.slice(5), // "2026-05" -> "05"
    receita: d.incomeRealizada,
    despesa: d.expenseRealizada,
  }));

  return (
    <CardShell title="Receita vs Despesa (6 meses)" icon={BarChart3}>
      <div className="h-full w-full">
        {isLoading ? (
          <div className="h-full w-full animate-pulse rounded bg-[var(--surface-muted)]/40" />
        ) : isError ? (
          <p className="text-xs text-[var(--fg-muted)]">
            Não foi possível carregar.
          </p>
        ) : rows.length === 0 || rows.every((r) => r.receita === 0 && r.despesa === 0) ? (
          <EmptyState
            icon={BarChart3}
            message="Sem movimentação nos últimos 6 meses."
          />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                stroke="var(--fg-muted)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="var(--fg-muted)"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => formatBrlCompact(Number(v))}
              />
              <Tooltip
                cursor={{ fill: 'var(--surface-muted)', opacity: 0.4 }}
                contentStyle={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: 'var(--fg)',
                }}
                labelStyle={{ color: 'var(--fg)' }}
                itemStyle={{ color: 'var(--fg)' }}
                formatter={(v) => formatBrlCompact(Number(v))}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                dataKey="receita"
                name="Receita"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="despesa"
                name="Despesa"
                fill="#ef4444"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </CardShell>
  );
}
